import { useEffect, useRef, useState } from "react";
import { FiCalendar, FiChevronDown, FiPlus, FiTrash2 } from "react-icons/fi";
import {
  birthDateMask,
  birthDateToIso,
  cpfMask,
  isoToBirthDate,
  phoneMask,
} from "../../../Utils/masks";
import { AddressFields } from "./AddressFields";
import { createEmptyOwner } from "./utils";

const inputClass =
  "individual-onboarding-input h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100";

const labelClass = "mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400";

const getOwnerLabel = (owner, index) => {
  const name = [owner.firstName, owner.lastName].filter(Boolean).join(" ").trim();
  return name || `Beneficiário ${index + 1}`;
};

const OwnerBirthDateField = ({ value, onChange }) => {
  const birthDatePickerRef = useRef(null);

  return (
    <label>
      <span className={labelClass}>Data de nascimento</span>
      <div className="relative">
        <input
          type="text"
          className={`${inputClass} pr-10`}
          value={value}
          onChange={(e) => onChange(birthDateMask(e.target.value))}
          placeholder="dd/mm/aaaa"
          inputMode="decimal"
          autoComplete="off"
          maxLength={10}
          required
        />
        <button
          type="button"
          className="absolute right-2 top-1/2 z-[1] flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-100 hover:text-[#e7a73f] dark:hover:bg-brand-dark-border dark:hover:text-brand-gold-mid"
          aria-label="Abrir calendário"
          onClick={() => {
            const picker = birthDatePickerRef.current;
            if (!picker) return;
            if (typeof picker.showPicker === "function") {
              picker.showPicker();
              return;
            }
            picker.click();
          }}
        >
          <FiCalendar className="h-4 w-4" />
        </button>
      </div>
      <input
        ref={birthDatePickerRef}
        type="date"
        className="individual-onboarding-hidden-date"
        tabIndex={-1}
        aria-hidden="true"
        value={birthDateToIso(value)}
        max={new Date().toISOString().slice(0, 10)}
        onChange={(e) => {
          if (!e.target.value) return;
          onChange(isoToBirthDate(e.target.value));
        }}
      />
    </label>
  );
};

export const BeneficialOwnersStep = ({
  owners,
  onChange,
  onOwnerZipCodeChange,
  cepLoadingByIndex,
  cepError,
}) => {
  const [expandedIndex, setExpandedIndex] = useState(0);

  useEffect(() => {
    if (expandedIndex >= owners.length) {
      setExpandedIndex(Math.max(0, owners.length - 1));
    }
  }, [expandedIndex, owners.length]);

  const updateOwner = (index, patch) => {
    onChange(owners.map((owner, i) => (i === index ? { ...owner, ...patch } : owner)));
  };

  const updateOwnerAddress = (index, address) => {
    updateOwner(index, { address });
  };

  const addOwner = () => {
    const nextIndex = owners.length;
    onChange([...owners, createEmptyOwner()]);
    setExpandedIndex(nextIndex);
  };

  const removeOwner = (index) => {
    if (owners.length <= 1) return;
    onChange(owners.filter((_, i) => i !== index));
    setExpandedIndex((current) => {
      if (current === index) return Math.max(0, index - 1);
      if (current > index) return current - 1;
      return current;
    });
  };

  const setControlPerson = (index, checked) => {
    onChange(
      owners.map((owner, i) => ({
        ...owner,
        isControlPerson: i === index ? checked : checked ? false : owner.isControlPerson,
      }))
    );
  };

  const togglePanel = (index) => {
    setExpandedIndex((current) => (current === index ? -1 : index));
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Informe os beneficiários finais da empresa. Pelo menos um deve ser pessoa de controle.
      </p>

      <div className="space-y-1.5">
        {owners.map((owner, index) => {
          const isExpanded = expandedIndex === index;
          const label = getOwnerLabel(owner, index);
          const hasAddressData = Boolean(owner.address.city?.trim() || owner.address.state?.trim());

          return (
            <div
              key={`owner-${index}`}
              className={`individual-onboarding-owner-panel ${
                isExpanded ? "individual-onboarding-owner-panel--expanded" : ""
              }`}
            >
              <div className="flex items-stretch">
                <button
                  type="button"
                  className={`individual-onboarding-owner-panel__trigger min-w-0 flex-1 ${
                    isExpanded ? "individual-onboarding-owner-panel__trigger--expanded" : ""
                  }`}
                  aria-expanded={isExpanded}
                  onClick={() => togglePanel(index)}
                >
                  <FiChevronDown
                    className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition dark:text-gray-500 ${
                      isExpanded ? "rotate-180 text-[#e7a73f] dark:text-brand-gold-mid" : ""
                    }`}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {label}
                  </span>
                  {owner.isControlPerson ? (
                    <span className="individual-onboarding-owner-panel__badge">
                      Pessoa de controle
                    </span>
                  ) : null}
                </button>

                {owners.length > 1 ? (
                  <button
                    type="button"
                    className="inline-flex shrink-0 items-center gap-1 border-l border-gray-100 px-2.5 text-xs font-medium text-red-500 transition hover:bg-red-50 hover:text-red-600 dark:border-brand-dark-border dark:hover:bg-red-950/30"
                    onClick={() => removeOwner(index)}
                    aria-label={`Remover ${label}`}
                  >
                    <FiTrash2 className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Remover</span>
                  </button>
                ) : null}
              </div>

              {isExpanded ? (
                <div className="space-y-3 border-t border-gray-100 px-3 py-3 dark:border-brand-dark-border sm:px-4">
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <label>
                      <span className={labelClass}>Nome</span>
                      <input
                        className={inputClass}
                        value={owner.firstName}
                        onChange={(e) => updateOwner(index, { firstName: e.target.value })}
                        required
                      />
                    </label>
                    <label>
                      <span className={labelClass}>Sobrenome</span>
                      <input
                        className={inputClass}
                        value={owner.lastName}
                        onChange={(e) => updateOwner(index, { lastName: e.target.value })}
                        required
                      />
                    </label>
                    <label>
                      <span className={labelClass}>CPF</span>
                      <input
                        className={inputClass}
                        value={owner.cpf}
                        onChange={(e) =>
                          updateOwner(index, {
                            cpf: cpfMask(e.target.value.replace(/\D/g, "").slice(0, 11)),
                          })
                        }
                        placeholder="000.000.000-00"
                        inputMode="numeric"
                        autoComplete="off"
                        required
                      />
                    </label>
                    <OwnerBirthDateField
                      value={owner.birthDate}
                      onChange={(birthDate) => updateOwner(index, { birthDate })}
                    />
                    <label>
                      <span className={labelClass}>E-mail</span>
                      <input
                        type="email"
                        className={inputClass}
                        value={owner.email}
                        onChange={(e) => updateOwner(index, { email: e.target.value })}
                        required
                      />
                    </label>
                    <label>
                      <span className={labelClass}>Telefone</span>
                      <input
                        className={inputClass}
                        value={owner.phone}
                        onChange={(e) => updateOwner(index, { phone: phoneMask(e.target.value) })}
                        required
                      />
                    </label>
                  </div>

                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-[#e7a73f] focus:ring-[#e7a73f]/30 dark:border-brand-dark-border dark:bg-brand-dark-surface"
                      checked={owner.isControlPerson}
                      onChange={(e) => setControlPerson(index, e.target.checked)}
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Pessoa de controle da empresa
                    </span>
                  </label>

                  <AddressFields
                    address={owner.address}
                    onChange={(address) => updateOwnerAddress(index, address)}
                    onZipCodeChange={(value) => onOwnerZipCodeChange(index, value)}
                    cepLoading={cepLoadingByIndex?.[index]}
                    cepError={cepError}
                    idPrefix={`owner-${index}-address`}
                    collapsible
                    collapsibleTitle="Endereço do beneficiário"
                    defaultExpanded={!hasAddressData}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="btn-outline inline-flex w-full items-center justify-center gap-2 sm:w-auto"
        onClick={addOwner}
      >
        <FiPlus className="h-4 w-4" />
        Adicionar beneficiário
      </button>
    </div>
  );
};
